import {
  CreateScenarioPayload,
  PollingScenarioDetails,
  PollingScenarioGeneral,
} from 'api/endpoints/emailPolling';
import { ProcessGeneral } from 'api/endpoints/processes';
import { ApiResponseAsync } from 'container/models';

function objectAssertPropertyType(
  obj: any,
  key: string,
  ...type: string[]
): boolean {
  if (!(key in obj) && type.indexOf('undefined') === -1) return false;
  const actualType = typeof obj[key];
  return type.indexOf(actualType) !== -1;
}

interface EmailFields {
  isValid?: boolean;
  email: string;
}

function isEmailFields(value: any): value is EmailFields {
  return (
    objectAssertPropertyType(value, 'isValid', 'boolean', 'undefined') &&
    objectAssertPropertyType(value, 'email', 'string')
  );
}

interface ImapFields extends EmailFields {
  password?: string;
}

function isImapFields(value: any): value is ImapFields {
  return (
    isEmailFields(value) &&
    objectAssertPropertyType(value, 'password', 'string', 'undefined')
  );
}

interface MSGraphFields extends EmailFields {
  tenantId: string;
  applicationId: string;
  userId: string;
  secret?: string;
}

function isMSGraphFields(value: any): value is MSGraphFields {
  return (
    isEmailFields(value) &&
    objectAssertPropertyType(value, 'tenantId', 'string') &&
    objectAssertPropertyType(value, 'applicationId', 'string') &&
    objectAssertPropertyType(value, 'userId', 'string') &&
    objectAssertPropertyType(value, 'secret', 'string', 'undefined')
  );
}

export type EmailProviderAuthFields = ImapFields | MSGraphFields;

type ImapProviders = 'gmail' | 'office365';
type MSGraphProviders = 'msgraph';
export type EmailProviders = ImapProviders | MSGraphProviders;

export function isValidAuthCombination(
  provider: EmailProviders,
  fields: EmailProviderAuthFields
): boolean {
  if (provider === 'gmail' || provider === 'office365')
    return isImapFields(fields);
  if (provider === 'msgraph') return isMSGraphFields(fields);

  return false;
}

export interface CheckCredententialsResult {
  isValid: boolean;
}

export interface GetLabelsResult {
  labels: string[];
}

export interface IEmailScenarioRepo {
  checkCredentials(
    provider: ImapProviders,
    auth: ImapFields
  ): ApiResponseAsync<CheckCredententialsResult>;
  checkCredentials(
    provider: MSGraphProviders,
    auth: MSGraphFields
  ): ApiResponseAsync<CheckCredententialsResult>;

  getLabels(
    provider: ImapProviders,
    auth: ImapFields
  ): ApiResponseAsync<GetLabelsResult>;
  getLabels(
    provider: MSGraphProviders,
    auth: MSGraphFields
  ): ApiResponseAsync<GetLabelsResult>;

  getScenarios(
    processDraft: ProcessGeneral
  ): ApiResponseAsync<DataWithTotal<PollingScenarioGeneral>>;

  getScenarioDetails(
    scenario: PollingScenarioGeneral
  ): ApiResponseAsync<PollingScenarioDetails>;

  activateScenario(scenario: PollingScenarioGeneral): ApiResponseAsync<unknown>;

  deactivateScenario(
    scenario: PollingScenarioGeneral
  ): ApiResponseAsync<unknown>;

  deleteScenario(scenario: PollingScenarioGeneral): ApiResponseAsync<unknown>;

  createScenario(
    processDraft: ProcessGeneral,
    newScenarioDetails: CreateScenarioPayload
  ): ApiResponseAsync<PollingScenarioDetails>;

  updateScenario(
    currentScenario: PollingScenarioGeneral,
    updatedScenarioDetails: CreateScenarioPayload
  ): ApiResponseAsync<PollingScenarioDetails>;
}
